import { Box, Grid, Stack, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  candidateSearchAdvancedQuery,
  ClientDTO,
  findByName,
  ICandidate,
  IJobCandidateInteraction,
} from 'wisestep-api';
import {
  formatDateInDayMonthYear,
  formatLocation,
  WsButton,
  WsColors,
  WsDataTable,
  WsTitleBanner,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { SubmissionListingDefaultQuery } from '../../submissions/submissionListing/SubmissionListingDefaultQuery';

const StyledStack = styled(Stack)({
  backgroundColor: WsColors.TheBlues50,
  gap: 1,
});

interface ISearchCandidate {
  clientName: string;
}

export const useConvertSubmissionToClosure = () => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const openClosureForm = async (candidate: ICandidate, placementType: string) => {
    if (candidate.companyName) {
      try {
        const response = await findByName(candidate.companyName);
        if (response.data) {
          const client: ClientDTO = response.data;
          navigate('/placements/add', {
            state: {
              candidate: candidate,
              client: client['data'],
              submissionId: candidate.id,
              placementType: placementType,
            },
          });
        }
      } catch (err) {
        setNotification(failed(`Something went wrong while fetching client details.`));
      }
    }
  };

  return openClosureForm;
};

const ClosureCandidateSubmission = () => {
  const openClosureForm = useConvertSubmissionToClosure();
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(0);
  const [tableData, setTableData] = useState<any>([]);
  const [query, setQuery] = useState<IJobCandidateInteraction>(SubmissionListingDefaultQuery);
  const methods = useForm<ISearchCandidate>();

  methods.register('clientName', {
    onChange: (e) => {
      const client: ClientDTO = e.target.value;
      if (client) {
        const updatedQuery: IJobCandidateInteraction = { ...query };
        updatedQuery.page = 0;
        updatedQuery.clientNames = client.name ? [client.name] : [];
        setQuery(updatedQuery);
      }
    },
  });

  useEffect(() => {
    const fetchSubmissionData = async (query: IJobCandidateInteraction) => {
      query.page = pageNo;
      const response = await candidateSearchAdvancedQuery(query);
      setTableData(response.data);
    };
    fetchSubmissionData(query);
  }, [query, pageNo]);

  const columns = [
    {
      Header: 'Candidate Name',
      accessor: 'name',
    },
    {
      Header: 'Candidate Location',
      accessor: 'currentLocation',
      Cell: (cell: Cell) => {
        const address = cell.row.original['currentLocation']
          ? formatLocation(cell.row.original['currentLocation'])
          : '';
        return <>{address}</>;
      },
    },
    {
      Header: 'Client Name',
      accessor: 'companyName',
    },
    {
      Header: 'Job Title',
      accessor: 'mappedJobTitle',
    },
    {
      Header: 'Job Location',
      accessor: 'mappedJobLocation',
    },
    {
      Header: 'Submission By',
      accessor: 'createdBy',
    },
    {
      Header: 'Submission Date',
      accessor: 'createdOn',
      Cell: (cell: Cell) => {
        const date = cell.row.original['createdOn']
          ? formatDateInDayMonthYear(new Date(cell.row.original['createdOn']))
          : '';
        return <>{date}</>;
      },
    },
    {
      Header: 'Action',
      accessor: 'someValue',
      Cell: (cell: Cell) => {
        return (
          <WsButton
            variant="contained"
            size="small"
            onClick={async () => {
              await openClosureForm(cell.row.original as ICandidate, 'PERMANENT');
              setQuery(query);
            }}
          >
            Create Closure
          </WsButton>
        );
      },
    },
  ];

  return (
    <Box borderRadius={1.5}>
      <StyledStack justifyContent="space-between">
        <Box>
          <WsTitleBanner
            title="Convert Submissions"
            backButton={true}
            goBack={() => navigate('/closures/createoptions')}
          />
          <FormProvider {...methods}>
            <Grid container spacing={1} mt={1} mb={1}>
              <Grid item xs={4}>
                <ClientAutocomplete multiple={false} name="clientName" />
              </Grid>
            </Grid>
          </FormProvider>
          <WsDataTable
            columns={columns}
            data={tableData as []}
            selectable={false}
            boldItems={['name']}
            pageable={true}
            totalDataItems={tableData ? tableData.length : 0}
            fetchPage={(page) => setPageNo(page)}
          />
        </Box>
      </StyledStack>
    </Box>
  );
};

export default ClosureCandidateSubmission;
